import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  SendOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Utilities from "../../../dependencies/custom/react-utilities";
import Settings from "../../../dependencies/custom/settings";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import useBursaryGiants from "../../../hooks/academics/useBursaryGiants";
import ValuesStore from "../../../store/values-store";

const TRACK_OPTIONS = [
  { value: "Bursary", label: "Bursary" },
  { value: "AcademicGiants", label: "Academic Giants" },
];

const BURSARY_REC_STATUSES = [
  "Pending Review",
  "Reviewed",
  "Submitted",
  "Rejected",
];
const GIANTS_REC_STATUSES = [
  "Pending Review",
  "Reviewed",
  "Approved",
  "Rejected",
];
const ALLOCATION_STATUSES = [
  "Pending Approval",
  "Approved",
  "Rejected",
  "Sent to Finance",
  "Processed",
];
const NOTIFICATION_STATUSES = ["Pending", "Sent", "Failed"];
const SCORE_TYPES = ["Average", "Total"];
const GIANTS_AWARD_CHOICES = ["Certificate", "Medal", "Trophy"];

const statusColor = (status) =>
  ({
    "Pending Review": "gold",
    Reviewed: "blue",
    Submitted: "purple",
    Approved: "green",
    Rejected: "red",
    "Pending Approval": "gold",
    "Sent to Finance": "cyan",
    Processed: "green",
    Pending: "gold",
    Sent: "green",
    Failed: "red",
    open: "blue",
    under_review: "gold",
    escalated: "orange",
    resolved: "green",
    closed: "default",
    appealed: "purple",
  })[status] || "default";

const fullName = (record) => record?.ranking?.studentName || "-";
const className = (record) => record?.ranking?.className || "-";
const rank = (record) => record?.ranking?.rankInClass || "-";
const awardChoice = (record) =>
  record?.awardChoice || record?.award_choice || "-";

const BursaryGiantsManagement = ({ financeOnly = false }) => {
  const valuesStore = ValuesStore();
  const myRoles =
    valuesStore.getValue("myRoles") ||
    JSON.parse(sessionStorage.getItem("myRoles") || "[]");
  const hasRole = (...roles) =>
    Array.isArray(myRoles) && roles.some((role) => myRoles.includes(role));
  const isAdmin = hasRole("System Administrator");
  const canDhtReview =
    isAdmin || hasRole("Deputy Head Teacher", "Deputy Principal");
  const canHtApprove =
    isAdmin || hasRole("Head Teacher", "Headteacher", "Principal", "Director");
  const canFinanceProcess =
    isAdmin ||
    hasRole("Head of Finance", "Bursar", "Assistant Bursar", "Accountant");

  const {
    loading,
    fetchBursarySettings,
    fetchGiantsSettings,
    saveBursarySettings,
    saveGiantsSettings,
    fetchRankings,
    triggerRankings,
    fetchBursaryRecommendations,
    fetchGiantsRecommendations,
    updateBursaryRecommendation,
    updateGiantsRecommendation,
    bulkBursaryReview,
    bulkBursaryReject,
    bulkBursarySubmit,
    bulkGiantsReview,
    bulkGiantsApprove,
    bulkGiantsReject,
    fetchAllocations,
    fetchDashboard,
    bulkAllocationApprove,
    bulkAllocationReject,
    bulkAllocationSendToFinance,
    bulkAllocationProcessed,
    fetchNotificationLogs,
    resendNotifications,
  } = useBursaryGiants();

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const [classes, setClasses] = useState([]);
  const [filters, setFilters] = useState({ track: "Bursary" });
  const [activeTab, setActiveTab] = useState(
    financeOnly ? "allocations" : "settings",
  );
  const [bursarySettings, setBursarySettings] = useState([]);
  const [giantsSettings, setGiantsSettings] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [bursaryRecs, setBursaryRecs] = useState([]);
  const [giantsRecs, setGiantsRecs] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [selectedLogKeys, setSelectedLogKeys] = useState([]);
  const [settingsModal, setSettingsModal] = useState({
    open: false,
    type: "bursary",
    record: null,
  });
  const [recommendationModal, setRecommendationModal] = useState({
    open: false,
    type: "bursary",
    record: null,
  });
  const [rejectModal, setRejectModal] = useState({
    open: false,
    action: null,
    title: "",
  });
  const [giantsApprovalModal, setGiantsApprovalModal] = useState({
    open: false,
    record: null,
  });
  const [form] = Form.useForm();
  const [recForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [giantsApprovalForm] = Form.useForm();

  const loadClasses = useCallback(async () => {
    const response = await Utilities.fetchWithAuth(
      "GET",
      Settings.urls.bootstrap.classes,
    );
    if (response?.ok) {
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setClasses(data);
    }
  }, []);

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
    loadClasses();
  }, [fetchAcademicYears, fetchTerms, loadClasses]);

  const load = useCallback(async () => {
    const common = {
      acadyrID: filters.acadyrID,
      termID: filters.termID,
      school_class: filters.school_class,
      status: filters.status,
    };

    if (!financeOnly && activeTab === "settings") {
      const [bursary, giants] = await Promise.all([
        fetchBursarySettings({
          acadyrID: filters.acadyrID,
          termID: filters.termID,
        }),
        fetchGiantsSettings({
          acadyrID: filters.acadyrID,
          termID: filters.termID,
        }),
      ]);
      setBursarySettings(bursary || []);
      setGiantsSettings(giants || []);
      return;
    }

    if (!financeOnly && activeTab === "rankings") {
      if (!filters.acadyrID || !filters.termID || !filters.track) return;
      setRankings(
        (await fetchRankings({ ...common, track: filters.track })) || [],
      );
      return;
    }

    if (!financeOnly && activeTab === "bursary") {
      setBursaryRecs((await fetchBursaryRecommendations(common)) || []);
      return;
    }

    if (!financeOnly && activeTab === "giants") {
      setGiantsRecs((await fetchGiantsRecommendations(common)) || []);
      return;
    }

    if (activeTab === "allocations") {
      const allocationFilters = financeOnly
        ? { ...common, status: filters.status || "Sent to Finance" }
        : common;
      setAllocations((await fetchAllocations(allocationFilters)) || []);
      if (filters.acadyrID && filters.termID) {
        setDashboard(
          await fetchDashboard({
            acadyrID: filters.acadyrID,
            termID: filters.termID,
          }),
        );
      }
      return;
    }

    if (activeTab === "notifications") {
      setSelectedLogKeys([]);
      setNotificationLogs((await fetchNotificationLogs(common)) || []);
    }
  }, [
    activeTab,
    fetchAllocations,
    fetchBursaryRecommendations,
    fetchBursarySettings,
    fetchDashboard,
    fetchGiantsRecommendations,
    fetchGiantsSettings,
    fetchNotificationLogs,
    fetchRankings,
    filters,
    financeOnly,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const openSettings = (type, record = null) => {
    setSettingsModal({ open: true, type, record });
    form.setFieldsValue(
      record || {
        scoreType: type === "bursary" ? "Average" : "Total",
        ...(type === "giants" ? { qualifiersPerStream: 8 } : {}),
        isActive: true,
      },
    );
  };

  const saveSettings = async () => {
    const values = await form.validateFields();
    const result =
      settingsModal.type === "bursary"
        ? await saveBursarySettings(values, settingsModal.record?.id)
        : await saveGiantsSettings(values, settingsModal.record?.id);
    if (result) {
      setSettingsModal({ open: false, type: "bursary", record: null });
      form.resetFields();
      load();
    }
  };

  const openRecommendation = (type, record) => {
    setRecommendationModal({ open: true, type, record });
    recForm.setFieldsValue({
      recommendedPercentage: record?.recommendedPercentage,
      remarks: record?.remarks,
    });
  };

  const saveRecommendation = async () => {
    const values = await recForm.validateFields();
    const result =
      recommendationModal.type === "bursary"
        ? await updateBursaryRecommendation(
            recommendationModal.record.id,
            values,
          )
        : await updateGiantsRecommendation(
            recommendationModal.record.id,
            values,
          );
    if (result) {
      setRecommendationModal({ open: false, type: "bursary", record: null });
      recForm.resetFields();
      load();
    }
  };

  const askReject = (title, action) => {
    setRejectModal({ open: true, title, action });
    rejectForm.resetFields();
  };

  const confirmReject = async () => {
    const { rejectionReason } = await rejectForm.validateFields();
    const result = await rejectModal.action(rejectionReason);
    if (result) {
      setRejectModal({ open: false, action: null, title: "" });
      load();
    }
  };

  const askApproveGiant = (record) => {
    setGiantsApprovalModal({ open: true, record });
    giantsApprovalForm.resetFields();
  };

  const confirmApproveGiant = async () => {
    const { awardChoice } = await giantsApprovalForm.validateFields();
    const result = await bulkGiantsApprove(
      [giantsApprovalModal.record.id],
      awardChoice,
    );
    if (result) {
      setGiantsApprovalModal({ open: false, record: null });
      load();
    }
  };

  const runRowAction = async (action, id) => {
    const result = await action([id]);
    if (result) load();
  };

  const canResendLog = (record) =>
    record?.allocation?.status === "Processed" && record?.status !== "Sent";

  const resendForLogs = (logs) => {
    const allocationIds = [
      ...new Set(logs.map((log) => log?.allocation?.id).filter(Boolean)),
    ];
    if (!allocationIds.length) {
      message.warning("No processed allocations to resend notifications for.");
      return;
    }
    Modal.confirm({
      title: "Resend parent notification emails",
      content: `This will queue notification emails for ${allocationIds.length} allocation(s). Continue?`,
      okText: "Resend",
      onOk: async () => {
        const result = await resendNotifications(allocationIds);
        if (result) {
          setSelectedLogKeys([]);
          load();
        }
      },
    });
  };

  const bursaryRecommendationActions = (record) => (
    <Space size={4} wrap>
      {canDhtReview &&
        ["Pending Review", "Reviewed"].includes(record.status) && (
          <Tooltip title="Edit recommendation">
            <Button
              size="small"
              type="link"
              icon={<EditOutlined />}
              onClick={() => openRecommendation("bursary", record)}
            />
          </Tooltip>
        )}
      {canDhtReview && record.status === "Pending Review" && (
        <Button
          size="small"
          icon={<CheckOutlined />}
          onClick={() => runRowAction(bulkBursaryReview, record.id)}
        >
          Review
        </Button>
      )}
      {canDhtReview && record.status === "Reviewed" && (
        <Button
          size="small"
          icon={<SendOutlined />}
          onClick={() => runRowAction(bulkBursarySubmit, record.id)}
        >
          Submit
        </Button>
      )}
      {canHtApprove &&
        ["Pending Review", "Reviewed", "Submitted"].includes(record.status) && (
          <Button
            size="small"
            danger
            icon={<CloseOutlined />}
            onClick={() =>
              askReject("Reject Bursary Recommendation", (reason) =>
                bulkBursaryReject([record.id], reason),
              )
            }
          >
            Reject
          </Button>
        )}
    </Space>
  );

  const giantsRecommendationActions = (record) => (
    <Space size={4} wrap>
      {canDhtReview &&
        ["Pending Review", "Reviewed"].includes(record.status) && (
          <Tooltip title="Edit recommendation">
            <Button
              size="small"
              type="link"
              icon={<EditOutlined />}
              onClick={() => openRecommendation("giants", record)}
            />
          </Tooltip>
        )}
      {canDhtReview && record.status === "Pending Review" && (
        <Button
          size="small"
          icon={<CheckOutlined />}
          onClick={() => runRowAction(bulkGiantsReview, record.id)}
        >
          Review
        </Button>
      )}
      {canHtApprove && record.status === "Reviewed" && (
        <Button
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          onClick={() => askApproveGiant(record)}
        >
          Approve
        </Button>
      )}
      {canHtApprove &&
        ["Pending Review", "Reviewed"].includes(record.status) && (
          <Button
            size="small"
            danger
            icon={<CloseOutlined />}
            onClick={() =>
              askReject("Reject Academic Giants Recommendation", (reason) =>
                bulkGiantsReject([record.id], reason),
              )
            }
          >
            Reject
          </Button>
        )}
    </Space>
  );

  const allocationActions = (record) => (
    <Space size={4} wrap>
      {!financeOnly && canHtApprove && record.status === "Pending Approval" && (
        <Button
          size="small"
          icon={<CheckOutlined />}
          onClick={() => runRowAction(bulkAllocationApprove, record.id)}
        >
          Approve
        </Button>
      )}
      {!financeOnly && canHtApprove && record.status === "Pending Approval" && (
        <Button
          size="small"
          danger
          icon={<CloseOutlined />}
          onClick={() =>
            askReject("Reject Bursary Allocation", (reason) =>
              bulkAllocationReject([record.id], reason),
            )
          }
        >
          Reject
        </Button>
      )}
      {!financeOnly && canHtApprove && record.status === "Approved" && (
        <Button
          size="small"
          icon={<SendOutlined />}
          onClick={() => runRowAction(bulkAllocationSendToFinance, record.id)}
        >
          Send to Finance
        </Button>
      )}
      {financeOnly &&
        canFinanceProcess &&
        record.status === "Sent to Finance" && (
          <Button
            size="small"
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => runRowAction(bulkAllocationProcessed, record.id)}
          >
            Mark Processed
          </Button>
        )}
    </Space>
  );

  const rankingColumns = [
    {
      title: "Rank",
      dataIndex: "rankInClass",
      width: 80,
      render: (value) => value || "-",
    },
    { title: "Student", dataIndex: "studentName", width: 180 },
    { title: "Class", dataIndex: "className", width: 120 },
    { title: "Score", dataIndex: "rankingScore", width: 110 },
    { title: "Score Type", dataIndex: "scoreType", width: 110 },
    {
      title: "Threshold",
      dataIndex: "meetsThreshold",
      width: 110,
      render: (v) => (
        <Tag color={v ? "green" : "red"}>{v ? "Met" : "Not met"}</Tag>
      ),
    },
    {
      title: "Flags",
      width: 180,
      render: (_, r) => (
        <Space>
          {r.hasIncompleteScores && <Tag color="orange">Incomplete</Tag>}
          {r.is_stale && <Tag color="red">Stale</Tag>}
          {r.hasRecommendation && <Tag color="blue">Recommended</Tag>}
        </Space>
      ),
    },
    {
      title: "Generated",
      dataIndex: "generatedAt",
      width: 150,
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
    },
  ];

  const recColumns = (type) => [
    { title: "Rank", width: 70, render: (_, r) => rank(r) },
    { title: "Student", width: 180, render: (_, r) => fullName(r) },
    { title: "Class", width: 110, render: (_, r) => className(r) },
    {
      title: "Score",
      width: 90,
      render: (_, r) => r.ranking?.rankingScore || "-",
    },
    ...(type === "bursary"
      ? [
          {
            title: "Recommended %",
            dataIndex: "recommendedPercentage",
            width: 130,
          },
        ]
      : []),
    ...(type === "giants"
      ? [{ title: "Award", width: 120, render: (_, r) => awardChoice(r) }]
      : []),
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (status) => <Tag color={statusColor(status)}>{status}</Tag>,
    },
    {
      title: "Reviewed By",
      dataIndex: "reviewedByName",
      width: 150,
      render: (v) => v || "-",
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Actions",
      width: 260,
      fixed: "right",
      render: (_, record) =>
        type === "bursary"
          ? bursaryRecommendationActions(record)
          : giantsRecommendationActions(record),
    },
  ];

  const allocationColumns = [
    {
      title: "Student",
      width: 180,
      render: (_, r) => fullName(r.recommendation),
    },
    {
      title: "Class",
      width: 110,
      render: (_, r) => className(r.recommendation),
    },
    { title: "Rank", width: 70, render: (_, r) => rank(r.recommendation) },
    {
      title: "Recommended %",
      width: 130,
      render: (_, r) => r.recommendation?.recommendedPercentage || "-",
    },
    {
      title: "Approved %",
      dataIndex: "approvedPercentage",
      width: 120,
      render: (v) => v || "-",
    },
    {
      title: "Approved Amount",
      dataIndex: "approvedAmount",
      width: 140,
      render: (v) => v || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (status) => <Tag color={statusColor(status)}>{status}</Tag>,
    },
    {
      title: "Approved By",
      dataIndex: "approvedByName",
      width: 150,
      render: (v) => v || "-",
    },
    {
      title: "Processed",
      dataIndex: "processedAt",
      width: 140,
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Actions",
      fixed: "right",
      width: 260,
      render: (_, record) => allocationActions(record),
    },
  ];

  const notificationColumns = [
    {
      title: "Student",
      dataIndex: "student_name",
      width: 180,
      render: (v) => v || "-",
    },
    {
      title: "Class",
      width: 110,
      render: (_, r) => className(r.allocation?.recommendation),
    },
    {
      title: "Recipient Email",
      dataIndex: "recipientEmail",
      width: 200,
      render: (v) => v || "-",
    },
    {
      title: "Allocation Status",
      width: 140,
      render: (_, r) => (
        <Tag color={statusColor(r.allocation?.status)}>
          {r.allocation?.status || "-"}
        </Tag>
      ),
    },
    {
      title: "Email Status",
      dataIndex: "status",
      width: 110,
      render: (status, r) => (
        <Tag color={statusColor(status)}>{r.status_display || status}</Tag>
      ),
    },
    {
      title: "Sent At",
      dataIndex: "sent_at",
      width: 150,
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
    },
    {
      title: "Failure Reason",
      dataIndex: "failureReason",
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Logged",
      dataIndex: "created_at",
      width: 150,
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
    },
    ...(canFinanceProcess
      ? [
          {
            title: "Actions",
            width: 120,
            fixed: "right",
            render: (_, record) =>
              canResendLog(record) && (
                <Button
                  size="small"
                  icon={<SendOutlined />}
                  onClick={() => resendForLogs([record])}
                >
                  Resend
                </Button>
              ),
          },
        ]
      : []),
  ];

  const settingsColumns = (type) => [
    { title: "Academic Year", dataIndex: "academicYear" },
    { title: "Term", dataIndex: "termName" },
    { title: "Score Type", dataIndex: "scoreType" },
    { title: "Threshold", dataIndex: "passThreshold" },
    { title: "Total Marks", dataIndex: "totalMarks" },
    ...(type === "bursary"
      ? [{ title: "Max %", dataIndex: "maxBursaryPercentage" }]
      : []),
    {
      title: "Active",
      dataIndex: "isActive",
      render: (v) => (
        <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => openSettings(type, record)}
        />
      ),
    },
  ];

  const selectedClass = useMemo(
    () => classes.find((item) => item.id === filters.school_class),
    [classes, filters.school_class],
  );

  const tabItems = [
    !financeOnly && {
      key: "settings",
      label: "Settings",
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card
              title="Bursary Settings"
              extra={
                <Button onClick={() => openSettings("bursary")}>New</Button>
              }
            >
              <Table
                size="small"
                columns={settingsColumns("bursary")}
                dataSource={bursarySettings}
                rowKey="id"
                pagination={false}
                scroll={{ x: 800 }}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title="Academic Giants Settings"
              extra={
                <Button onClick={() => openSettings("giants")}>New</Button>
              }
            >
              <Table
                size="small"
                columns={settingsColumns("giants")}
                dataSource={giantsSettings}
                rowKey="id"
                pagination={false}
                scroll={{ x: 760 }}
              />
            </Card>
          </Col>
        </Row>
      ),
    },
    !financeOnly && {
      key: "rankings",
      label: "Rankings",
      children: (
        <Card>
          <Space style={{ marginBottom: 12 }}>
            <Button
              icon={<SyncOutlined />}
              onClick={() =>
                triggerRankings({
                  school_class: filters.school_class,
                  acadyrID: filters.acadyrID,
                  termID: filters.termID,
                })
              }
              disabled={
                !filters.school_class || !filters.acadyrID || !filters.termID
              }
            >
              Trigger Ranking
              {selectedClass?.name ? ` for ${selectedClass.name}` : ""}
            </Button>
          </Space>
          <Table
            columns={rankingColumns}
            dataSource={rankings}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1200 }}
          />
        </Card>
      ),
    },
    !financeOnly && {
      key: "bursary",
      label: "Bursary Recommendations",
      children: (
        <Card>
          <Table
            columns={recColumns("bursary")}
            dataSource={bursaryRecs}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1300 }}
          />
        </Card>
      ),
    },
    !financeOnly && {
      key: "giants",
      label: "Academic Giants",
      children: (
        <Card>
          <Table
            columns={recColumns("giants")}
            dataSource={giantsRecs}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1200 }}
          />
        </Card>
      ),
    },
    {
      key: "allocations",
      label: financeOnly ? "Bursary Allocations" : "Allocations",
      children: (
        <>
          {dashboard && !financeOnly && (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Approved Not Sent"
                    value={dashboard?.alerts?.approved_not_sent_to_finance || 0}
                  />
                </Card>
              </Col>
              {(dashboard?.allocations || []).map((item) => (
                <Col xs={12} md={6} key={item.status}>
                  <Card size="small">
                    <Statistic title={item.status} value={item.count} />
                  </Card>
                </Col>
              ))}
            </Row>
          )}
          <Card>
            <Table
              columns={allocationColumns}
              dataSource={allocations}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1300 }}
            />
          </Card>
        </>
      ),
    },
    {
      key: "notifications",
      label: "Notification Logs",
      children: (
        <Card>
          {canFinanceProcess && (
            <Space style={{ marginBottom: 12 }}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                disabled={!selectedLogKeys.length}
                onClick={() =>
                  resendForLogs(
                    notificationLogs.filter((log) =>
                      selectedLogKeys.includes(log.id),
                    ),
                  )
                }
              >
                Resend Selected ({selectedLogKeys.length})
              </Button>
            </Space>
          )}
          <Table
            columns={notificationColumns}
            dataSource={notificationLogs}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1300 }}
            rowSelection={
              canFinanceProcess
                ? {
                    selectedRowKeys: selectedLogKeys,
                    onChange: setSelectedLogKeys,
                    getCheckboxProps: (record) => ({
                      disabled: !canResendLog(record),
                    }),
                  }
                : undefined
            }
          />
        </Card>
      ),
    },
  ].filter(Boolean);

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h4 style={{ margin: 0 }}>
          {financeOnly ? "Bursary Allocations" : "Bursary & Academic Giants"}
        </h4>
        <Button icon={<SyncOutlined />} onClick={load}>
          Refresh
        </Button>
      </div>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Academic Year"
            allowClear
            style={{ width: 170 }}
            value={filters.acadyrID}
            onChange={(v) => setFilters((prev) => ({ ...prev, acadyrID: v }))}
            options={academicYears.map((y) => ({
              value: y.id,
              label: y.acadyr,
            }))}
          />
          <Select
            placeholder="Term"
            allowClear
            style={{ width: 150 }}
            value={filters.termID}
            onChange={(v) => setFilters((prev) => ({ ...prev, termID: v }))}
            options={terms.map((t) => ({ value: t.id, label: t.name }))}
          />
          <Select
            placeholder="Class"
            allowClear
            style={{ width: 170 }}
            value={filters.school_class}
            onChange={(v) =>
              setFilters((prev) => ({ ...prev, school_class: v }))
            }
            options={classes.map((c) => ({
              value: c.id,
              label: c.name || c.className,
            }))}
          />
          {!financeOnly && activeTab === "rankings" && (
            <Select
              style={{ width: 180 }}
              value={filters.track}
              onChange={(v) => setFilters((prev) => ({ ...prev, track: v }))}
              options={TRACK_OPTIONS}
            />
          )}
          {["bursary", "giants", "allocations", "notifications"].includes(
            activeTab,
          ) && (
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 190 }}
              value={filters.status}
              onChange={(v) => setFilters((prev) => ({ ...prev, status: v }))}
              options={(activeTab === "bursary"
                ? BURSARY_REC_STATUSES
                : activeTab === "giants"
                  ? GIANTS_REC_STATUSES
                  : activeTab === "notifications"
                    ? NOTIFICATION_STATUSES
                    : ALLOCATION_STATUSES
              ).map((s) => ({ value: s, label: s }))}
            />
          )}
        </Space>
      </Card>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

      <Modal
        title={`${settingsModal.record ? "Edit" : "New"} ${settingsModal.type === "bursary" ? "Bursary" : "Academic Giants"} Settings`}
        open={settingsModal.open}
        onOk={saveSettings}
        onCancel={() =>
          setSettingsModal({ open: false, type: "bursary", record: null })
        }
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="acadyrID"
            label="Academic Year"
            rules={[{ required: true }]}
          >
            <Select
              options={academicYears.map((y) => ({
                value: y.id,
                label: y.acadyr,
              }))}
            />
          </Form.Item>
          <Form.Item name="termID" label="Term" rules={[{ required: true }]}>
            <Select
              options={terms.map((t) => ({ value: t.id, label: t.name }))}
            />
          </Form.Item>
          <Form.Item
            name="scoreType"
            label="Score Type"
            rules={[{ required: true }]}
          >
            <Select
              options={SCORE_TYPES.map((s) => ({ value: s, label: s }))}
            />
          </Form.Item>
          <Form.Item
            name="passThreshold"
            label="Pass Threshold"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="totalMarks"
            label="Total Marks"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          {settingsModal.type === "bursary" && (
            <Form.Item
              name="maxBursaryPercentage"
              label="Max Bursary %"
              rules={[{ required: true }]}
            >
              <InputNumber min={0.01} max={100} style={{ width: "100%" }} />
            </Form.Item>
          )}
          {settingsModal.type === "giants" && (
            <Form.Item
              name="qualifiersPerStream"
              label="Qualifiers Per Stream"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          )}
          <Form.Item
            name="isActive"
            label="Status"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: true, label: "Active" },
                { value: false, label: "Inactive" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Recommendation Review"
        open={recommendationModal.open}
        onOk={saveRecommendation}
        onCancel={() =>
          setRecommendationModal({ open: false, type: "bursary", record: null })
        }
        destroyOnClose
      >
        <Form form={recForm} layout="vertical">
          {recommendationModal.type === "bursary" && (
            <Form.Item
              name="recommendedPercentage"
              label="Recommended %"
              rules={[{ required: true }]}
            >
              <InputNumber min={0.01} max={100} style={{ width: "100%" }} />
            </Form.Item>
          )}
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Approve Academic Giant"
        open={giantsApprovalModal.open}
        onOk={confirmApproveGiant}
        onCancel={() => setGiantsApprovalModal({ open: false, record: null })}
        destroyOnClose
      >
        <Form form={giantsApprovalForm} layout="vertical">
          <Form.Item
            name="awardChoice"
            label="Award Choice"
            rules={[
              { required: true, message: "Please select an award choice." },
            ]}
          >
            <Select
              options={GIANTS_AWARD_CHOICES.map((choice) => ({
                value: choice,
                label: choice,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={rejectModal.title}
        open={rejectModal.open}
        onOk={confirmReject}
        onCancel={() =>
          setRejectModal({ open: false, action: null, title: "" })
        }
        destroyOnClose
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="rejectionReason"
            label="Rejection Reason"
            rules={[{ required: true, min: 5 }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BursaryGiantsManagement;
